export interface EventPage<T, Cursor> {
    rows: T[];
    first?: Cursor;
    last?: Cursor;
    full: boolean;
}

// Sliding page window. Old responses cannot write into a new country session.
export class EventWindow<T, Cursor> {
    pages: EventPage<T, Cursor>[] = [];
    offset = 0;
    busy = false;
    older = true;
    newer = false;
    error = '';
    private disposed = false;
    constructor(private load: (direction: 'next' | 'previous', cursor: Cursor) => Promise<EventPage<T, Cursor>>,
        private changed: () => void, private maxPages = 3) {}

    latest(page: EventPage<T, Cursor>) {
        if (this.disposed || this.busy)
            return;
        if (this.offset || this.pages.length > 1) {
            this.newer = true;
            this.changed();
            return;
        }
        this.pages = [page];
        this.older = page.full;
        this.changed();
    }

    async move(direction: 'next' | 'previous') {
        if (this.disposed || this.busy || !this.pages.length || (direction === 'next' && !this.older) || (direction === 'previous' && !this.offset))
            return;
        const cursor = direction === 'next' ? this.pages.at(-1)!.last : this.pages[0].first;
        if (cursor === undefined)
            return;
        this.busy = true;
        this.error = '';
        this.changed();
        try {
            const page = await this.load(direction, cursor);
            if (this.disposed)
                return;
            if (direction === 'next') {
                this.older = page.full;
                if (page.first !== undefined) {
                    this.pages.push(page);
                    if (this.pages.length > this.maxPages) {
                        this.pages.shift();
                        this.offset++;
                    }
                }
            } else if (page.first !== undefined) {
                this.pages.unshift(page);
                this.offset = Math.max(0, this.offset - 1);
                if (this.pages.length > this.maxPages) {
                    this.pages.pop();
                    this.older = true;
                }
            } else
                this.offset = 0;
        } catch {
            if (!this.disposed)
                this.error = 'Could not load events. Please retry.';
        } finally {
            if (!this.disposed) {
                this.busy = false;
                this.changed();
            }
        }
    }

    dispose() { this.disposed = true; this.pages = []; }
}
